/**
 * 로그인 페이지 컴포넌트
 * - 전화번호 기반 Firebase Phone Authentication
 * - 서비스 약관 및 개인정보 처리방침 동의
 *
 * @see SPEC-001-firebase-phone-auth.md
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { VerificationCodePage } from './VerificationCodePage';
import { useLoginForm, useFirebasePhoneAuth, useWhatsAppAuth } from '@/hooks';
import { useLanguage } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { loginPhoneAuthTranslations } from '@/i18n/loginPage';
import { apiClient } from '@/lib/api/client';
import { MarketingConsentReview } from '@/components/privacy/MarketingConsentReview';
import type { AuthUser } from '@/lib/api/types';
import {
  AuthHeader,
  PhoneNumberInput,
  SmsAuthButton,
  AgreementSection,
  LoginLogo,
} from './auth';

type AuthChannel = 'sms' | 'whatsapp';

const LEGAL_DOCUMENT_LINKS = {
  termsOfService: '/legal/terminos-condiciones.pdf',
  privacyPolicy: '/legal/aviso-privacidad.pdf',
} as const;

interface LoginPageProps {
  onBack: () => void;
  onLogin: (email: string, password: string) => void;
  onLoginComplete?: () => void;
}

/**
 * 로그인 페이지 컴포넌트
 * - 전화번호 기반 Firebase Phone Authentication
 * - 서비스 약관 및 개인정보 처리방침 동의
 */
export function LoginPage({ onBack, onLogin, onLoginComplete }: LoginPageProps) {
  const { language, locale } = useLanguage();
  const t = loginPhoneAuthTranslations[locale];
  const form = useLoginForm(language);
  const { completeCustomerLogin, isLoggedIn, user, customerId } = useAuth();
  const [pendingCompletion, setPendingCompletion] = useState<{ channel: AuthChannel; identityEpoch: number; lifecycle: number } | null>(null);
  const identityEpoch = apiClient.getAuthIdentityEpoch();
  const owner = useMemo(() => ({ identityEpoch, isLoggedIn, customerId: user?.id ?? customerId }), [identityEpoch, isLoggedIn, user?.id, customerId]);
  const ownerRef = useRef<typeof owner | null>(null);
  useLayoutEffect(() => {
    ownerRef.current = owner;
    return () => { if (ownerRef.current === owner) ownerRef.current = null; };
  }, [owner]);
  const lifecycleRef = useRef(0);
  useLayoutEffect(() => {
    lifecycleRef.current += 1;
    return () => { lifecycleRef.current += 1; };
  }, []);
  // #522: 훅이 15 locale 전수 지원 — locale 직접 전달
  const firebaseAuth = useFirebasePhoneAuth(locale);
  const whatsappAuth = useWhatsAppAuth(locale);
  const [authChannel, setAuthChannel] = useState<AuthChannel>('sms');
  const [apiError, setApiError] = useState<string | null>(null);
  const isSendingAnyOtp = firebaseAuth.isSendingCode || whatsappAuth.isSendingOtp;

  const getFullPhoneNumber = useCallback(() => {
    return `${form.selectedCountry.code}${form.phoneNumber}`;
  }, [form.selectedCountry.code, form.phoneNumber]);

  const handleSendSmsCode = useCallback(async () => {
    setAuthChannel('sms');
    setApiError(null);

    const success = await firebaseAuth.sendVerificationCode(getFullPhoneNumber());
    if (success) {
      form.setShowVerificationPage(true);
    } else {
      setApiError(firebaseAuth.error);
    }
  }, [firebaseAuth, form, getFullPhoneNumber]);

  const handleSendWhatsAppCode = useCallback(async () => {
    setAuthChannel('whatsapp');
    setApiError(null);

    const result = await whatsappAuth.sendOtp(getFullPhoneNumber());
    if (result.success) {
      form.setShowVerificationPage(true);
    } else {
      setApiError(result.error);
    }
  }, [form, getFullPhoneNumber, whatsappAuth]);

  // 인증 코드 확인 핸들러
  const handleVerify = useCallback(
    async (code: string) => {
      const lifecycle = lifecycleRef.current;
      const attemptIdentity = apiClient.getAuthIdentityEpoch();
      if (ownerRef.current !== owner) return;
      let verifiedWhatsAppUser: AuthUser | null = null;
      let success = false;

      if (authChannel === 'sms') {
        success = await firebaseAuth.confirmCode(code);
      } else {
        const response = await whatsappAuth.verifyOtp(getFullPhoneNumber(), code);
        // WhatsApp does not own an auth transition until this response is applied.
        // A newer committed account must not be replaced by its late verification.
        if (ownerRef.current !== owner || apiClient.getAuthIdentityEpoch() !== attemptIdentity) return;
        verifiedWhatsAppUser = response?.user ?? null;
        success = Boolean(verifiedWhatsAppUser);
      }

      if (lifecycleRef.current !== lifecycle) return;
      if (success) {
        if (authChannel === 'whatsapp' && verifiedWhatsAppUser) {
          completeCustomerLogin(verifiedWhatsAppUser);
        }
        if (form.agreeSecondary) {
          // The pre-login checkbox is intent only: never grant an unseen notice.
          setPendingCompletion({ channel: authChannel, identityEpoch: apiClient.getAuthIdentityEpoch(), lifecycle });
        } else if (authChannel === 'whatsapp') {
          onLoginComplete?.();
        } else {
          onLogin('firebase-phone-auth', 'firebase-verified');
        }
      }
      // 실패 시 VerificationCodePage에서 에러 표시
    },
    [
      authChannel,
      completeCustomerLogin,
      firebaseAuth,
      form.agreeSecondary,
      getFullPhoneNumber,
      onLoginComplete,
      onLogin,
      owner,
      whatsappAuth,
    ]
  );

  // OTP 재발송 핸들러
  const handleResendCode = useCallback(async (): Promise<{ success: boolean }> => {
    if (authChannel === 'whatsapp') {
      return whatsappAuth.sendOtp(getFullPhoneNumber());
    }

    const success = await firebaseAuth.sendVerificationCode(getFullPhoneNumber());
    return { success };
  }, [authChannel, firebaseAuth, getFullPhoneNumber, whatsappAuth]);

  // 뒤로 가기 핸들러 (인증 코드 페이지에서)
  const handleBackFromVerification = useCallback(() => {
    form.setShowVerificationPage(false);
    firebaseAuth.reset();
    whatsappAuth.clearErrors();
  }, [firebaseAuth, form, whatsappAuth]);

  const canComplete = pendingCompletion !== null && isLoggedIn &&
    pendingCompletion.identityEpoch === apiClient.getAuthIdentityEpoch() &&
    pendingCompletion.lifecycle === lifecycleRef.current;
  const completionIsCurrent = () => canComplete && ownerRef.current === owner &&
    pendingCompletion?.identityEpoch === apiClient.getAuthIdentityEpoch() &&
    pendingCompletion.lifecycle === lifecycleRef.current;
  if (pendingCompletion !== null && canComplete) {
    return <MarketingConsentReview
      key={`${pendingCompletion.identityEpoch}:${user?.id ?? customerId}`}
      customerKey={user?.id ?? customerId ?? null}
      locale={locale}
      isCurrent={completionIsCurrent}
      onContinue={() => {
        if (pendingCompletion.channel === 'whatsapp') onLoginComplete?.();
        else onLogin('firebase-phone-auth', 'firebase-verified');
      }}
    />;
  }

  // 인증 코드 페이지 표시
  if (form.showVerificationPage) {
    return (
      <VerificationCodePage
        onBack={handleBackFromVerification}
        onVerify={handleVerify}
        onResend={handleResendCode}
        deliveryChannel={authChannel}
        phoneNumber={form.getFullPhoneNumber()}
        isVerifying={authChannel === 'sms' ? firebaseAuth.isVerifying : whatsappAuth.isVerifying}
        verifyError={authChannel === 'sms' ? firebaseAuth.error : whatsappAuth.verifyError}
        attemptsRemaining={authChannel === 'sms' ? null : whatsappAuth.attemptsRemaining}
        isSendingOtp={authChannel === 'sms' ? firebaseAuth.isSendingCode : whatsappAuth.isSendingOtp}
        sendOtpError={authChannel === 'sms' ? firebaseAuth.error : whatsappAuth.sendOtpError}
      />
    );
  }

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      {/* Header */}
      <AuthHeader onBack={onBack} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Logo Section - Green Hero (LoginLogo가 hero 영역만 렌더링) */}
        <LoginLogo />

        {/* White Card Container */}
        <div className="flex-1 bg-background rounded-t-[35px] -mt-[clamp(2rem,8vw,3rem)] px-[min(5vw,1.25rem)] pt-[clamp(2rem,8vw,3rem)] pb-[min(5vw,1.25rem)] font-['Noto_Sans',sans-serif]">
          {/* Welcome Text */}
          <div className="text-center mb-[clamp(1.5rem,6vw,2rem)]">
            <h2 className="text-[clamp(20px,5vw,24px)] font-bold text-foreground mb-2">
              {t.welcomeTitle}
            </h2>
            <p className="text-[clamp(13px,3.5vw,15px)] text-muted-foreground">
              {t.phoneIntro}
            </p>
          </div>

          {/* Phone Number Input */}
          <PhoneNumberInput
            phoneNumber={form.phoneNumber}
            onPhoneNumberChange={form.handlePhoneNumberChange}
            selectedCountry={form.selectedCountry}
            showCountryDropdown={form.showCountryDropdown}
            onToggleCountryDropdown={() => form.setShowCountryDropdown(!form.showCountryDropdown)}
            countrySearch={form.countrySearch}
            onCountrySearchChange={form.setCountrySearch}
            filteredCountries={form.filteredCountries}
            onSelectCountry={form.handleSelectCountry}
            onCloseCountryDropdown={form.handleCloseCountryDropdown}
            getCountryName={form.getCountryName}
          />

          {/* API 에러 메시지 */}
          {(apiError || firebaseAuth.error) && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400 text-center">
                {apiError || firebaseAuth.error}
              </p>
            </div>
          )}

          {/* SMS Auth Button (Firebase Phone Auth) */}
          <SmsAuthButton
            isEnabled={form.isButtonEnabled && !isSendingAnyOtp}
            isLoading={firebaseAuth.isSendingCode}
            onClick={handleSendSmsCode}
          />

          <SmsAuthButton
            isEnabled={form.isButtonEnabled && !isSendingAnyOtp}
            isLoading={whatsappAuth.isSendingOtp}
            onClick={handleSendWhatsAppCode}
            label={t.getCodeViaWhatsApp}
            variant="outline"
          />

          {/* Agreement Section — LFPDPPP Primary/Secondary 토글 포함 (#372) */}
          <AgreementSection
            agreeWhatsApp={form.agreeWhatsApp}
            setAgreeWhatsApp={form.setAgreeWhatsApp}
            agreeTerms={form.agreeTerms}
            setAgreeTerms={form.setAgreeTerms}
            agreePrivacy={form.agreePrivacy}
            setAgreePrivacy={form.setAgreePrivacy}
            isAllAgreed={form.isAllAgreed}
            onAgreeAll={form.handleAgreeAll}
            termsOfServiceHref={LEGAL_DOCUMENT_LINKS.termsOfService}
            privacyPolicyHref={LEGAL_DOCUMENT_LINKS.privacyPolicy}
            agreePrimary={form.agreePrimary}
            setAgreePrimary={form.setAgreePrimary}
            agreeSecondary={form.agreeSecondary}
            setAgreeSecondary={form.setAgreeSecondary}
            avisoHref={LEGAL_DOCUMENT_LINKS.privacyPolicy}
          />
        </div>
      </div>

      {/* reCAPTCHA Container (invisible) */}
      <div id="recaptcha-container" />

    </div>
  );
}
